/* eslint-disable react/no-this-in-sfc */
import React, { useState, useContext } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import ConfigContext from '../../../contexts/ConfigContext';
import NinModal from './NinModal';
import { sendToast } from '../../../utils/displayUtils';
import { COMMENT_MAX_LENGTH, INITIAL_SELECT_ID } from '../../../utils/Constants';
import AmContractSignerForm, { AmContractSignerFormType } from './AmContractSignerForm';
import { replaceContractSigner } from '../../../api/actionMemoApi';

export interface AmContractSignerFormBaseProps {
    title: String;
    formType: AmContractSignerFormType;
    toggleButton: JSX.Element;
    onSubmit?: () => void;
}

export interface AmContractSignerModalProps extends AmContractSignerFormBaseProps {
    amId: number;
    contractSignerName: string | null;
}

const submitAmContractSigner = async (
    config: LoruleRequestConfig,
    formType: AmContractSignerFormType,
    values: {
        contractOrganizationId: number | null;
        contractSigner: number | null;
        contractSignerName: string | null;
        commentMessage: string | null;
    },
    amId: number,
    contractSignerName: string | null,
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        if (formType === AmContractSignerFormType.ADD) {
            await replaceContractSigner(
                config,
                amId,
                values.contractOrganizationId !== INITIAL_SELECT_ID
                    ? values.contractOrganizationId
                    : null,
                values.contractSigner !== INITIAL_SELECT_ID ? values.contractSigner : null,
                values.commentMessage ? values.commentMessage : null,
            );
            sendToast(
                <>
                    Added Contract Signer: <strong>{values.contractSignerName}</strong>
                </>,
            );
        } else {
            await replaceContractSigner(
                config,
                amId,
                values.contractOrganizationId !== INITIAL_SELECT_ID
                    ? values.contractOrganizationId
                    : null,
                values.contractSigner !== INITIAL_SELECT_ID ? values.contractSigner : null,
                values.commentMessage ? values.commentMessage : null,
            );
            if (values.contractSigner !== INITIAL_SELECT_ID) {
                sendToast(
                    <>
                        Replaced Contract Signer: <strong>{values.contractSignerName}</strong>
                    </>,
                );
            } else {
                sendToast(
                    <>
                        Removed Contract Signer: <strong>{contractSignerName}</strong>
                    </>,
                );
            }
        }

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(
                `${
                    formType === AmContractSignerFormType.ADD ? 'Add' : 'Replace'
                } contract signer failed:  ${error.message}`,
            );
        }
        setSubmitting(false);
        return false;
    }
};

const AmContractSignerModal = (props: AmContractSignerModalProps) => {
    const config = useContext(ConfigContext);

    const [responseError, setResponseError] = useState('');

    const addCommentValidationSchema = Yup.object().shape({
        contractOrganizationId: Yup.string().test(
            'contact organization id validation',
            'Contract entity is required',
            function test() {
                return (
                    this.parent.signatureRequired === INITIAL_SELECT_ID ||
                    this.parent.contractOrganizationId > 0
                );
            },
        ),
        contractSigner: Yup.string().test(
            'contact organization id validation',
            'Contract signer is required',
            function test() {
                return (
                    this.parent.signatureRequired === INITIAL_SELECT_ID ||
                    this.parent.contractSigner > 0
                );
            },
        ),
        commentMessage: Yup.string()
            // eslint-disable-next-line no-template-curly-in-string
            .max(COMMENT_MAX_LENGTH, 'Comment exceeds max length of ${max} characters'),
    });

    const initialValues = {
        signatureRequired:
            props.formType === AmContractSignerFormType.REPLACE ? INITIAL_SELECT_ID : 1,
        contractOrganizationId: INITIAL_SELECT_ID,
        contractSigner: INITIAL_SELECT_ID,
        contractSignerName: '',
        commentMessage: '',
    };

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={addCommentValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitAmContractSigner(
                            config,
                            props.formType,
                            values,
                            props.amId,
                            props.contractSignerName,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={(formikProps) => (
                        <AmContractSignerForm
                            {...formikProps}
                            formType={props.formType}
                            responseError={responseError}
                            setResponseError={setResponseError}
                            toggleModal={toggleModal}
                        />
                    )}
                />
            )}
        />
    );
};

export default AmContractSignerModal;
